import React, {useState} from 'react';
import styles from './SignUpForm.module.scss';
import {FormHelperText, Grid} from '@material-ui/core';
import {Form, Formik} from 'formik';
import * as Yup from "yup";
import TermsOfServiceCheckbox from 'gui/components/termsOfServiceCheckBox/TermsOfServiceCheckbox';
import FormikPasswordInput from 'gui/components/formikPasswordInput/FormikPasswordInput';
import FormikPhoneInput from 'gui/components/formikPhoneInput/FormikPhoneInput';
import {allowOnlyEnglishAutoConvert} from 'data/utils/shortcuts';
import FormikErrorScrollToTop from 'formik-top-error-focus';
import FormikTextInput from 'gui/components/formikTextInput';
import NewsLettersCheckbox from 'gui/components/newsLettersCheckBox';
import Recaptcha from 'gui/components/recaptcha/Recaptcha';
import NextButton from 'gui/components/nextButton/NextButton';
import {omit} from "lodash";
import FormikTextField from 'gui/components/formikTextField';
import FormikExternalPropsListener from 'gui/components/formikExternalPropsListener/FormikExternalPropsListener';

interface ISignUpFormProps {
  onSubmit: (formValues) => Promise<any>;
  emailUniqueError?: boolean;
  invalidRecaptcha?: boolean;
  nextText: string;
  loading?: boolean;
}

const retypeEmailRandomName = `retypeEm${new Date().getTime() + Math.round(Math.random() * 1000)}`;

const retypePasswordRandomName = `retype-new-password-${new Date().getTime() + Math.round(Math.random() * 1000)}`;


const SignUpForm = ({loading, ...props}: ISignUpFormProps) => {
  const [currentEmails, setLastCheckedEmails] = useState([]);
  return (
    <Formik
      initialValues={{
        email: "",
        [retypeEmailRandomName]: "",
        password: "",
        [retypePasswordRandomName]: "",
        firstName: "",
        lastName: "",
        phone: "",
        allowAdvertisementEmails: false,
        acceptedTerms: false,
        recaptcha: '',
        emailUniqueError: false,
      }}
      validationSchema={Yup.object({
        email: Yup.string()
          .min(4, 'מייל קצר מדיי')
          .max(60, 'מייל ארוך מדיי')
          .email("מייל לא תקין")
          .required("נדרש מייל")
          .when('emailUniqueError', (emailUniqueError, schema) => schema.test({
            test: email => !(emailUniqueError && currentEmails.includes(email)),
            message: "מייל זה כבר קיים"
          })),
        [retypeEmailRandomName]: Yup.string()
          .min(4, 'מייל קצר מדיי')
          .max(60, 'מייל ארוך מדיי')
          .oneOf([Yup.ref('email'), null], 'מייל לא זהה')
          .required("נדרש להזין מייל שוב"),
        password: Yup.string()
          .min(8, 'סיסמה קצרה מדיי, מינימום 8 אותיות או ספרות')
          .max(80, 'סיסמה ארוכה מדיי')
          .required('נדרשת סיסמה')
          .matches(/[a-zA-Z0-9]/, 'סיסמה יכולה רק להכיל אותיות באנגלית ומספרים'),
        [retypePasswordRandomName]: Yup.string()
          .min(8, 'סיסמה קצרה מדיי, מינימום 8 אותיות או ספרות')
          .max(80, 'סיסמה ארוכה מדיי')
          .oneOf([Yup.ref('password'), undefined], 'סיסמה צריכה להיות תואמת')
          .required("נדרש להזין סיסמה שוב"),
        firstName: Yup.string()
          .min(2, "שם פרטי צריך להיות 2 אותיות לפחות")
          .max(15, "שם פרטי יכול להיות עד 15 אותיות")
          .required("נדרש שם פרטי"),
        lastName: Yup.string()
          .min(2, "שם משפחה צריך להיות 2 אותיות לפחות")
          .max(20, "שם משפחה יכול להיות עד 20 אותיות")
          .required("נדרש שם משפחה"),
        phone: Yup.string()
          .min(8, "יש להזין מספר נייד")
          .max(20, "יש להזין מספר נייד")
          .required("יש להזין מספר נייד"),
        allowAdvertisementEmails: Yup.boolean(),
        acceptedTerms: Yup.boolean()
          .required("Required")
          .oneOf([true], "יש לאשר את התנאים לפני השימוש"),
        recaptcha: Yup.string().min(1),
      })}
      onSubmit={async (values, {setSubmitting, setFieldError, setFieldTouched}) => {
        if (props.emailUniqueError && !currentEmails.includes(values.email)) {
          return;
        }

        setLastCheckedEmails([values.email]);
        try {
          await props
            .onSubmit({
              ...omit(values, [retypeEmailRandomName, retypePasswordRandomName, 'invalidRecaptcha', 'emailUniqueError']),
              retypeEmail: values.email,
              retypePassword: values[retypePasswordRandomName],
            });
        } catch (e) {
          setSubmitting(false);
        }
      }}
    >
      {({submitForm, isSubmitting, setFieldValue, values, touched, errors, submitCount, setFieldError}) => (
        <Form translate={null}
              className={`signUpForm ${styles.signUpForm}`}>
          <FormikExternalPropsListener
            listenProps={[props.emailUniqueError, props.invalidRecaptcha]}
            onChange={async (form, [emailUniqueError, invalidRecaptcha]) => {
              await form.setFieldValue("emailUniqueError", emailUniqueError);
              await form.validateField("email");
            }}
          />
          <Grid container spacing={2}>
            <Grid item style={{marginTop: 15}} xs={12} sm={6}>
              <FormikTextField
                label="שם פרטי(אנגלית)"
                name="firstName"
                autoComplete="fname"
                onChange={(e) => {
                  setFieldValue('firstName', allowOnlyEnglishAutoConvert(e.target.value).toUpperCase());
                }}
                loading={loading}
              />
            </Grid>
            <Grid item style={{marginTop: 15}} xs={12} sm={6}>
              <FormikTextField
                label="שם משפחה(אנגלית)"
                name="lastName"
                autoComplete="lname"
                onChange={(e) => {
                  setFieldValue('lastName', allowOnlyEnglishAutoConvert(e.target.value).toUpperCase());
                }}
                loading={loading}
              />
            </Grid>

            <Grid item style={{marginTop: 15}} xs={12} sm={6}>
              <FormikTextField
                label="מייל"
                name="email"
                autoComplete="email"
                onChange={(e) => {
                  setFieldValue('email', (e.target.value || '').toLowerCase());
                }}
                loading={loading}
              />
            </Grid>
            <Grid item style={{marginTop: 15}} xs={12} sm={6}>
              <FormikTextField
                label="אימות כתובת מייל"
                name={retypeEmailRandomName}
                autoComplete={retypeEmailRandomName}
                loading={loading}
              />
            </Grid>

            <Grid item style={{marginTop: 15}} xs={12} sm={6}>
              <FormikPasswordInput
                label="סיסמה *"
                name="password"
                loading={loading}
              />
            </Grid>
            <Grid item style={{marginTop: 15}} xs={12} sm={6}>
              <FormikPasswordInput
                label="סיסמה שוב *"
                name={retypePasswordRandomName}
                loading={loading}
              />
            </Grid>

            <Grid item style={{marginTop: 15}} xs={12} sm={6}>
              <FormikPhoneInput
                label="מספר טלפון"
                name="phone"
                loading={loading}
              />
            </Grid>
            <Grid item style={{marginTop: 15}} xs={12} sm={6}/>

            <Grid item className={styles.termsContainer} xs={12} md={12}>
              <TermsOfServiceCheckbox loading={loading}/>
              <NewsLettersCheckbox loading={loading}/>

            </Grid>

            <Grid item xs={12}>
              {props.invalidRecaptcha ?
                <FormHelperText style={{textAlign: 'center'}} error>אימות רובוט נכשל</FormHelperText> : null}
              <Recaptcha loading={loading}
                         onVerify={verifyCode => {
                           setFieldValue('recaptcha', verifyCode, false)
                         }}/>
            </Grid>
            <Grid item style={{marginTop: 5}} xs={12}>
              <NextButton
                isDisabled={isSubmitting}
                onClick={submitForm}
                loading={loading}
              >
                {props.nextText}
              </NextButton>
            </Grid>
          </Grid>
          <FormikErrorScrollToTop/>
        </Form>
      )}
    </Formik>
  );
};

SignUpForm.defaultProps = {
  onSubmit: (formValues) => new Promise((resolve) => {
    
    setTimeout(resolve, 1000);
  }),
  emailUniqueError: false,
  nextText: "הרשם",
  loading: false,
};

export default React.memo(SignUpForm);
